import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import "./OurTeam.scss";

const teamData = [
  {
    name: "Sanjeev Jain",
    role: "Managing Partner",
    description:
      "Sanjeev Jain is a Fellow Chartered Accountant and Registered Insolvency Professional with over 30 years of strategic finance and governance leadership across logistics, telecom, manufacturing, and infrastructure. As former CFO of GATI and AFL/FedEx, he has led EBITDA improvement, business transformation, and operational efficiency programs for mid-to-large enterprises, and brings strong expertise in financial strategy, M&A, dispute resolution, and regulatory matters. As Managing Partner at Amicus Growth Advisors, he leads CFO and Board Advisory engagements covering turnarounds, fundraising and investor relations, due diligence, and performance improvement. He also serves on the boards of high-growth companies in the logistics and infrastructure sectors, supporting long-term strategy and value creation for promoters and leadership teams.",
    image: "/sk_jain.png",
    linkedin: "https://www.linkedin.com/in/sanjeev-jain-b7636314/",
  },
  {
    name: "Vikas Pawar",
    role: "Partner",
    description:
      "Vikas is a corporate strategy and supply chain transformation leader with 18+ years of experience across Gati, Adani and consulting assignments. He leads Amicus’ logistics and supply chain advisory mandates across network design, warehousing and transportation optimization, 3PL/4PL model design, vendor ecosystem governance, and KPI-led operating model implementation. His work typically includes diagnostics and benchmarking, cost analysis, RFP/RFQ and commercial structuring, and executive dashboards to ensure measurable outcomes.",
    image: "/vikas.png",
    linkedin: "https://www.linkedin.com/in/vikasvpawar/",
  },
  {
    name: "Sachin Mittal",
    role: "Associate Partner",
    description:
      "Sachin Mittal is an Associate Partner at Amicus Growth Advisors, bringing over six years of professional experience spanning strategy, financial services, and business advisory. His career has been anchored in high-stakes environments, including tenures with leading NBFCs and banking institutions, where he developed a sharp understanding of credit ecosystems, financial operations, and organizational dynamics. With three years dedicated to core strategy roles, Sachin has built a strong foundation in translating complex business challenges into structured, actionable plans. At Amicus, Sachin leads senior client engagements across business transformation, go-to-market strategy, and turnaround advisory for SMEs. He combines the analytical rigour of his financial services background with a practitioner’s instinct for implementation — helping clients navigate inflection points with clarity and conviction. His work is defined by a commitment to delivering measurable outcomes and building lasting advisory relationships.",
    image: "/images/our-team/sachin-mittal.webp",
    linkedin:
      "https://www.linkedin.com/in/ca-sachien?utm_source=share_via&utm_content=profile&utm_medium=member_ios",
  },
  {
    name: "Mukund Taparia",
    role: "Senior Consultant",
    description:
      "Mukund Taparia is a Senior Consultant and a Chartered Accountant with a passion for dance. He brings experience in financial analysis, translating data into practical business decisions. He works closely with founders on strategy and transformation, supporting them in scaling sustainably, along with transaction advisory and M&A.",
    image: "/images/our-team/mukund.webp",
    linkedin:
      "https://www.linkedin.com/in/mukund-taparia/?originalSubdomain=in",
  },
  {
    name: "Juhi Sheth",
    role: "Senior Consultant",
    description:
      "Juhi Sheth is a Chartered Accountant and CFA Level I cleared professional with experience in fund advisory, transaction support, and portfolio company monitoring. She has worked on pre-deal screening and valuation, Shareholders’ Agreement reviews, and monthly financial performance evaluation of investee companies. She also brings experience in CFO advisory and M&A support, with a strong foundation in financial analysis.",
    image: "/images/our-team/juhi-sheth.webp",
    linkedin:
      "https://www.linkedin.com/in/juhi-sheth-b67aab167/?originalSubdomain=in",
  },
  {
    name: "Kushal Purohit",
    role: "Senior Consultant",
    description:
      "CA Kushal Purohit is a sport enthusiast and a Chartered Accountant with expertise in investor fundraising and performance analytics, while also driving CFO-level insights through data dashboards and operational reviews. \n He works closely with management teams to drive practical and result-oriented solutions.",
    image: "/images/our-team/kaushal.webp",
    linkedin: "https://www.linkedin.com/in/ca-kushal-purohit-189664236/",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: [0.215, 0.61, 0.355, 1],
    },
  }),
};

const TeamMemberCard = ({ member, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <motion.div
      className={`team-member-card ${isExpanded ? "expanded" : ""}`}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={cardVariants}
    >
      <div className="card-inner">
        <a
          href={member.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="linkedin-icon-mobile"
        >
          <LinkedInIcon />
        </a>

        <div className="member-image">
          <img src={member.image} alt={member.name} />
        </div>

        <div className="info-container">
          <div className="top-info">
            <div className="text">
              <h4>{member.name}</h4>
              <p className="role">{member.role}</p>
            </div>
            {/* Desktop LinkedIn icon */}
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="linkedin-icon-desktop"
            >
              <LinkedInIcon />
            </a>
            {/* Mobile/Tablet expand button */}
            <button
              className="expand-btn"
              onClick={toggleExpand}
              aria-label={isExpanded ? "Collapse details" : "Expand details"}
            >
              {isExpanded ? <RemoveIcon /> : <AddIcon />}
            </button>
          </div>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                className="expanded-content mobile-expanded"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
              >
                <p>{member.description}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Desktop expanded content (hover based) */}
          <div className="expanded-content desktop-expanded">
            <p>{member.description}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const OurTeam = () => {
  return (
    <section className="our-team-section">
      <div className="container">
        <div className="team-header">
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="subtitle"
          >
            Our Team
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Meet the Advisors Leading Our <br /> Strategy & Finance
          </motion.h2>
        </div>

        <div className="team-grid">
          {teamData.map((member, index) => (
            <TeamMemberCard key={index} member={member} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OurTeam;
